import { useCallback } from "react";
import ProTable, { ProColumns } from "@ant-design/pro-table";
import Space from "antd/lib/space";
import Popconfirm from "antd/es/popconfirm";
import Button from "antd/es/button";
import DeleteIcon from "@ant-design/icons/DeleteOutlined";
import EditIcon from "@ant-design/icons/EditOutlined";
import {
    ICombo,
    ILookupKeys,
    useFetchSingleItem,
    Endpoints,
    ILookup,
    useLookups,
} from "components/api";

import { TaskDetailRequest } from "./task-detail-form";
import { MenuActions } from "pages/tasks/details";
import { Authorize } from "components/auth";

type TableProps = {
    lookups: {
        data: Record<string, ICombo[]>;
        isloading: boolean;
    };
    taskActivityId: string | null;
    deleteHandler: (id: number, type: MenuActions) => void;
    editHandler: (payload: TaskDetailRequest, type: MenuActions) => void;
    handlerMenuClick: (payload: TaskDetailRequest, type: MenuActions) => void;
};
type ResponseType = {
    id: number;
    description: string;
    task: ILookup;
    startDate: Date;
    endDate: Date;
    priority: ILookup;
    employee: ILookup;
    status: ILookup;
};
const TaskDetails = (props: TableProps) => {
    const endpoint = `${Endpoints.TaskDetails.main
        }?taskActivityId=${props.taskActivityId!}`;
    const { data, isError } = useFetchSingleItem<ResponseType[]>({
        endpoint,
        enabled: true,
        ids: null,
    });

    const columns = useCallback(
        (
            lookups: Record<ILookupKeys, ICombo[]>,
            isLoading: boolean
        ): ProColumns<ResponseType>[] => {
            if (isLoading) {
                return [];
            }
            return [
                {
                    title: "Description",
                    dataIndex: "description",
                },
                {
                    title: "Responsible",
                    dataIndex: "employee",
                    render: (_, { employee }) => employee ? employee.name : "",
                },
                {
                    title: "Start Date",
                    dataIndex: "startDate",
                    valueType: "date",
                },
                {
                    title: "End Date",
                    dataIndex: "endDate",
                    valueType: "date",
                },
                {
                    title: "Priority",
                    dataIndex: "priority",
                    render: (_, { priority }) => priority.name,
                },
                {
                    title: "Status",
                    dataIndex: "status",
                    render: (_, { status }) => status.name,
                },
                {
                    title: "Operations",
                    width: 200,
                    key: "options",
                    valueType: "option",
                    render: (_, record) => {
                        return (
                            <Space size="middle">
                                {Authorize({
                                    claim: "CTd",
                                }) && (
                                        <Popconfirm
                                            title={`Are you sure to delete the selected task from the system?`}
                                            onConfirm={() => props.deleteHandler(record.id, "delete")}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button
                                                danger
                                                type="primary"
                                                icon={<DeleteIcon />}
                                                title="Delete"
                                            />
                                        </Popconfirm>
                                    )}

                                {Authorize({ claim: 'CTd' }) && <Button
                                    onClick={() => {
                                        const { ...details } = record;
                                        props.editHandler(
                                            {
                                                ...details,
                                                taskActivityId: parseInt(props.taskActivityId!),
                                                status: parseInt(details.status.id.toString()),
                                                priority: parseInt(details.priority.id.toString()),
                                                employeeId: parseInt(details.employee.id.toString()),
                                            },
                                            "edit"
                                        );
                                    }}
                                    type="primary"
                                    icon={<EditIcon />}
                                    title="Edit"
                                />
                                }
                                {Authorize({ claim: 'UTd' }) && record.status.name === "Created" && <Button
                                    onClick={() => {
                                        const { ...details } = record;
                                        props.handlerMenuClick(
                                            {
                                                ...details,
                                                taskActivityId: parseInt(props.taskActivityId!),
                                                status: parseInt(details.status.id.toString()),
                                                priority: parseInt(details.priority.id.toString()),
                                            },
                                            "inprogress"
                                        );
                                    }}
                                    type="link"

                                    title="Mark InProgress"
                                >InProgress</Button>
                                }


                                {Authorize({ claim: 'UTd' }) &&
                                    record.status.name === "InProgress" && <Button
                                        onClick={() => {
                                            const { ...details } = record;
                                            props.handlerMenuClick(
                                                {
                                                    ...details,
                                                    taskActivityId: parseInt(props.taskActivityId!),
                                                    status: parseInt(details.status.id.toString()),
                                                    priority: parseInt(details.priority.id.toString()),
                                                },
                                                "done"
                                            );
                                        }}
                                        className="ant-btn ant-btn-ant-alert-info"
                                        type="link"

                                        title="Done"
                                    >Done</Button>
                                }

                            </Space>
                        );
                    },
                },
            ];
        },
        [props]
    );
    if (isError || !data) {
        return <div>Error</div>;
    }
    const addButton = Authorize({ claim: 'CTd' }) ? <Button
        onClick={() => {
            props.handlerMenuClick(
                {
                    taskActivityId: parseInt(props.taskActivityId!),
                    status: 0,
                    priority: 0,
                    description: "",
                    employeeId: 0,

                },
                "add"
            );
        }}
        type="primary"
        icon={<EditIcon />}
        title="Add Details"

    >Add Details</Button> : null;

    return (
        <ProTable<ResponseType>
            headerTitle={addButton}
            columns={columns(props.lookups.data, props.lookups.isloading)}
            loading={false}
            request={async () => data?.data}
            dataSource={data?.data ?? []}
            rowKey="id"
            search={false}
            dateFormatter="string"
            pagination={false}
        />
    );
};

export default TaskDetails;
